import { deriveFontStyle, type Font, type FontFamily } from '@bynder-studio/structured-text';
import type { FontFamily as FontFamilyDto, FontStyle as FontStyleDto, LegacyFontStyleDto } from 'packages/types/fonts';

type SourceFont = FontStyleDto | LegacyFontStyleDto;

// Both the new `/list` payload and the deprecated `galleryFonts` families narrow to this.
type SourceFamily = { name: string; fonts: SourceFont[] };

// `id` is the lowest reference id, never the DTO's physical `id`: the reference id is what every
// design persists, and it is embedded in `@font-face` family names, asset-loader cache keys and the
// exported render spec. Gallery fonts predate the mapping layer and their `id` already is one.
const toFont = (font: SourceFont, isActive = true): Font => {
    const referenceIds = 'referenceIds' in font ? font.referenceIds : [font.id];

    return {
        id: referenceIds[0],
        referenceIds,
        name: font.name ?? '',
        tag: font.tag ?? '',
        url: font.url ?? '',
        weight: font.weight,
        style: deriveFontStyle(font),
        includeLineGap: font.includeLineGap,
        isActive: 'isActive' in font ? font.isActive : isActive,
    };
};

export const toFontFamilies = (families: SourceFamily[], isActive = true): FontFamily[] =>
    families.map((family) => ({
        name: family.name,
        fonts: family.fonts.map((f) => toFont(f, isActive)),
    }));

// Read off the DTO rather than the render model: `isDefault` is not a rendering concern, so it stays
// out of `Font` the way `gallery`/`global` did. Routed through `toFont` so the id returned here is by
// construction the same `Font.id` the store holds — a hand-rolled `referenceIds[0]` could drift from it.
// Returns undefined when no font carries the flag, which happens: the backend tolerates a customer
// with no default and falls back to their most recently created font. The caller owns that fallback.
export const findDefaultFontId = (families: FontFamilyDto[]): Font['id'] | undefined => {
    const font = families.flatMap((family) => family.fonts).find((style) => style.isDefault);

    return font && toFont(font).id;
};
